import { httpClient } from '@cocoplatform/coco-rtc-client';
import React, {
  CSSProperties,
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import MSLogo from '!!react-svg-loader!./microsoft-logo.svg';
import GoogLogo from '!!react-svg-loader!./google-logo.svg';
import Separator from 'components/prelogin-layout/Separator';
import { useHistory } from 'react-router-dom';
import ROUTES from 'constants/routes';
import Button from '@mui/material/Button';
import TextField, { FilledTextFieldProps } from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import { EMAIL_REGEX } from 'utils/validation';
import FormHelperText from '@mui/material/FormHelperText';
import { reportServerError } from 'utils/report-error';
import { useAuthState } from 'utils/auth-state';
import GoogleSignInButton from 'components/social-login-buttons/GoogleSignIn';
import SocialLoginButtons from 'components/social-login-buttons/SocialLoginButtons';
import * as qs from 'query-string';
import { Box } from '@mui/material';
import { Trans, msg } from '@lingui/macro';
import { browserStorage } from 'utils/browser-storage';
import { useLingui } from '@lingui/react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import C from './NewSignupForm.css';

export type LoginFormVariant = 'signIn' | 'signUp';



export function BasicDatePicker(
  p: {
    value: Dayjs | null,
    setValue: (val: Dayjs | null) => void,
  }
) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label="Date of birth"
        value={p.value}
        onChange={(newValue) => {
          p.setValue(newValue);
        }}
        renderInput={(params) => <TextField {...params} />}
      />
    </LocalizationProvider>
  );
}

export default function NewSignupForm(p: {
  title?: string | false;
  signInLink?: string;
  onSuccess?: (data: { email: string }) => void;
  onSubmit?: (data: { email: string }) => void;
  onError?: () => void;
  variant: LoginFormVariant;
  style?: CSSProperties;
  InputProps?: FilledTextFieldProps['InputProps'];
  communityId?: string;
}) {
  const { _ } = useLingui();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dob, setDob] = React.useState<Dayjs | null>(null);

  const [isDispatching, setDispatching] = useState(false);
  const [isTouched, setTouched] = useState(false);
  const auth = useAuthState();

  const isValid = useMemo(() => {
    return email && EMAIL_REGEX.test(email);
  }, [email]);

  const showErr = isTouched && !isValid;

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('email')) {
      setEmail(queryParams.get('email')!);
      queryParams.delete('email');
      history.replace({
        search: queryParams.toString(),
      });
    }
  }, []);

  return (
    <form onSubmit={handleSubmit} className={C.container} autoComplete='off'>
      {p.title !== false && (
        <h1 className={C.primaryHeader}>
          {p.title ??
            (p.variant === 'signIn' ? (
              <Trans>Sign in</Trans>
            ) : (
              <Trans>Sign up to CoCo!</Trans>
            ))}
        </h1>
      )}
      <div
        style={{
          textAlign: 'center',
        }}
        className={C.emailHint}
      >
        <Trans>
          Please setup a username and a password and provide us with your date of birth.
        </Trans>
      </div>
      {/*<ControlLabel htmlFor='email_control'>Email</ControlLabel>*/}
      <TextField
        id='username'
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
        type='username'
        placeholder='Enter your username'
        variant='filled'
        autoFocus={true}
        fullWidth
        error={showErr}
        style={{
          borderRadius: '4px',
          border: '1px solid var(--color-primary)',
          ...p.style,
        }}
        InputProps={{
          style: {
            background: 'var(--color-base)',
            color: 'var(--color-text)',
            ...p.InputProps?.style,
          },
        }}
      />
      <Box mt={2} />
      <TextField
        id='password'
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
        type='password'
        placeholder='Enter your password'
        variant='filled'
        autoFocus={true}
        fullWidth
        error={showErr}
        style={{
          borderRadius: '4px',
          border: '1px solid var(--color-primary)',
          ...p.style,
        }}
        InputProps={{
          style: {
            background: 'var(--color-base)',
            color: 'var(--color-text)',
            ...p.InputProps?.style,
          },
        }}
      />

      <Box mt={2} />
      <BasicDatePicker setValue={setDob} value={dob} />

      <LoadingButton
        style={{ marginTop: '1rem', fontWeight: 'var(--font-weight-bold)' }}
        loading={isDispatching}
        size='large'
        fullWidth
        variant='contained'
        color='primary'
        type='submit'
      >
        <Trans>Sign up</Trans>
      </LoadingButton>
      {p.signInLink === undefined ? null : p.variant === 'signUp' ? (
        <div className={C.altVariantLink}>
          <Trans>Already using CoCo?</Trans>{' '}
          <a
            onClick={async () => {
              history.push(p.signInLink as string);
            }}
          >
            <Trans>Sign in</Trans>
          </a>
        </div>
      ) : (
        <div className={C.altVariantLink}>
          <Trans>New to CoCo?</Trans>{' '}
          <a
            onClick={async () => {
              await import('components/sign-up-page/SignUpPage');
              history.push(ROUTES.SIGN_UP);
            }}
          >
            <Trans>Sign up</Trans>
          </a>
        </div>
      )}
      {/*{p.variant === 'signUp' && (
        <div className={C.tocExpl}>
          By continuing, you agree to CoCo's Terms of Use and Privacy Policy
        </div>
      )} */}
    </form>
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.stopPropagation();
    e.preventDefault();
    if (!isTouched && !isValid) {
      setTouched(true);
      return;
    }
    p.onSubmit?.({ email });
    setDispatching(true);
    try {
      const res = await httpClient.post('/auth/simple-signup', {
        email,
        password,
        dob,
      });
      auth.saveToken(res.data.token);
    } catch (error) {
      reportServerError({
        title: _(msg`Failed to send login code`),
        error,
      });
      p.onError?.();
    } finally {
      setDispatching(false);
    }
  }
}
